import React from 'react';
import { InfoCircle } from '@/public/icons/tsx';
import { Typography } from '../typography';
import { Toggle } from './toggle';
import {
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';

import clsx from 'clsx';
import { ModalVerification } from '@/components/dashboard';
import { verificationsService } from '@/api';
import { formatErrorMessage } from '@/lib';
import { useNotification } from '@/hooks';
import { ErrorHttpResponse } from '@/interfaces';

import AppRoutes from '@/const/routes';
import Link from 'next/link';

interface ToggleCustomProps {
	name: string;
	label: string;
	watch: UseFormWatch<FieldValues>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
	disabled: boolean;
	className?: string;
	isDisabledButton: () => void;
	phoneNumber: string;
	refetch: () => void;
}

export const ToggleCustom: React.FC<ToggleCustomProps> = ({
	name,
	label,
	register,
	setValue,
	watch,
	disabled,
	className,
	isDisabledButton,
	setDisabled,
	phoneNumber,
	refetch,
}) => {
	const { toastSuccess, toastError } = useNotification();
	const [isVerifyingPhone, setIsVerifyingPhone] =
		React.useState<boolean>(false);
	const [errorVerification, setErrorVerification] = React.useState<
		string | React.ReactNode
	>('');

	const validatePhoneNumber = async (): Promise<void> => {
		try {
			await verificationsService.verifyNumber('phone_verification');
			toastSuccess('Verification code has been successfully sent.');
			setIsVerifyingPhone(true);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};
	const sentCodeAgain = async (): Promise<void> => {
		try {
			await verificationsService.verifyNumber('phone_verification');
			toastSuccess('Verification code has been successfully sent.');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};
	const validateOtpCode = async (code: string): Promise<void> => {
		try {
			await verificationsService.validatePhoneOtp(code, 'phone_verification');
			toastSuccess('Your phone number has been successfully verified.');
			setDisabled(false);
			refetch();
			setIsVerifyingPhone(false);
		} catch (error) {
			const status = (error as ErrorHttpResponse).statusCode;
			if (!status) {
				toastError(formatErrorMessage(undefined));
				setIsVerifyingPhone(false);
			} else if ((error as ErrorHttpResponse).statusCode === 400) {
				setErrorVerification(
					<span>
						The verification code has expired. <br />
						Please request a new one and try again.
					</span>
				);
			} else {
				setErrorVerification(
					'The verification code is incorrect. Please try again.'
				);
			}
		}
	};
	const lastTwoDigits = String(phoneNumber)?.slice(-2);

	return (
		<>
			<div className={clsx('flex items-center gap-4 mt-5', className)}>
				<div onClick={isDisabledButton}>
					<Toggle
						name={name}
						isActive={watch(name)}
						register={register}
						setValue={setValue}
						isDisabled={disabled}
					/>
				</div>
				<Typography type="regular-14" className="text-black">
					{label}
				</Typography>
			</div>
			{disabled && phoneNumber && (
				<div className="flex items-center gap-x-1.5 mt-[18.5px]">
					<InfoCircle fill="#FF0043" width="16" height="16" />
					<Typography type="regular-14" className="text-main-red">
						You need to{' '}
						<span
							className="font-semibold text-primary cursor-pointer"
							onClick={validatePhoneNumber}
						>
							Verify your phone
						</span>{' '}
						first.
					</Typography>
				</div>
			)}
			{disabled && !phoneNumber && (
				<div className="flex items-center gap-x-1.5 mt-[18.5px]">
					<InfoCircle fill="#FF0043" width="16" height="16" />
					<Typography type="regular-14" className="text-main-red">
						You need to{' '}
						<Link
							className="font-semibold text-primary"
							href={AppRoutes.CLIENTAPP_SETTINGS_PERSONAL}
						>
							add a phone number
						</Link>{' '}
						first.
					</Typography>
				</div>
			)}

			<ModalVerification
				title="Verify your phone"
				description={
					<div>
						<Typography type="regular-16" className="opacity-60">
							{`We've sent a code to your phone number ending in`}
						</Typography>
						<Typography type="regular-16" className="opacity-60">
							{`XXXXXXX${lastTwoDigits}`}
						</Typography>
					</div>
				}
				sentCodeAgain={sentCodeAgain}
				isOpen={isVerifyingPhone}
				setIsOpen={setIsVerifyingPhone}
				setIsModalOpen={setIsVerifyingPhone}
				error={errorVerification}
				setErrorVerification={setErrorVerification}
				validateOtpCode={validateOtpCode}
				numDigits={4}
			/>
		</>
	);
};
